import React from 'react';
import { DeleteOutlined, ReloadOutlined, UserOutlined } from '@ant-design/icons';
import type { GoogleUser } from '../App';
import type { AgentTask } from '../types';
import ApiTokensView from './ApiTokensView';
import ProfileConnectorsView from './ProfileConnectorsView';
import SecretsManagerView from './SecretsManagerView';

interface Props {
  user: GoogleUser | null;
  archivedTasks: AgentTask[];
  archivedKnowledgeBases: Array<{
    id: string;
    name: string;
    description?: string;
    status?: string;
    created_at?: string;
    updated_at?: string;
    document_count?: number;
    chunk_count?: number;
  }>;
  loading: boolean;
  kbLoading: boolean;
  busyTaskId: string | null;
  busyKbId: string | null;
  
  onRestoreTask: (taskId: string) => void;
  onDeleteTask: (taskId: string) => void;
  onRestoreKnowledgeBase: (kb: { id: string; name: string }) => void;
  onDeleteKnowledgeBase: (kb: { id: string; name: string }) => void;
}

const getTaskSummary = (task: AgentTask) => {
  const summary = task.final_answer?.trim();
  if (!summary) return 'No final result was saved for this task.';
  return summary.length > 180 ? `${summary.slice(0, 180)}…` : summary;
};

const getArchivedLabel = (task: AgentTask) => {
  const archivedAt = task.updated_at || task.completed_at || task.created_at;
  return archivedAt ? `Archived ${new Date(archivedAt).toLocaleString()}` : 'Archived task';
};

const ProfileView: React.FC<Props> = ({
  user,
  archivedTasks,
  archivedKnowledgeBases,
  loading,
  kbLoading,
  busyTaskId,
  busyKbId,
  onRestoreTask,
  onDeleteTask,
  onRestoreKnowledgeBase,
  onDeleteKnowledgeBase,
}) => (
  <div className="praxys-view-panel">
    <div className="praxys-view-header">
      <UserOutlined className="praxys-view-icon" />
      <h2 className="praxys-view-title">Profile</h2>
      <span className="praxys-view-subtitle">Account details and archived tasks</span>
    </div>

    <section className="praxys-profile-summary">
      {user?.picture ? (
        <img
          src={user.picture}
          alt={`Profile picture of ${user.name}`}
          className="praxys-user-avatar praxys-profile-avatar"
          referrerPolicy="no-referrer"
        />
      ) : (
        <div className="praxys-user-avatar praxys-user-avatar--initials praxys-profile-avatar">
          {user?.name?.charAt(0).toUpperCase() || 'U'}
        </div>
      )}
      <div className="praxys-profile-summary-copy">
        <h3>{user?.name || 'User'}</h3>
        <p>{user?.email || 'Signed in to Praxys.ai'}</p>
      </div>
    </section>

    <section className="praxys-profile-archives">
      <div className="praxys-profile-section-header">
        <h3>Archived Tasks</h3>
        <span>{archivedTasks.length} task{archivedTasks.length === 1 ? '' : 's'}</span>
      </div>

      {loading ? (
        <div className="praxys-view-empty">Loading archived tasks…</div>
      ) : archivedTasks.length === 0 ? (
        <div className="praxys-view-empty">No archived tasks yet.</div>
      ) : (
        <div className="praxys-profile-archive-list">
          {archivedTasks.map(task => (
            <div key={task.id} className="praxys-profile-archive-item">
              <div className="praxys-profile-archive-copy">
                <div className="praxys-profile-archive-topline">
                  <h4>{task.goal}</h4>
                  <span className="praxys-profile-archive-badge">{task.status}</span>
                </div>
                <p className="praxys-profile-archive-meta">{getArchivedLabel(task)}</p>
                <p className="praxys-profile-archive-summary">{getTaskSummary(task)}</p>
              </div>
              <div className="praxys-profile-archive-actions">
                <button
                  type="button"
                  className="praxys-profile-action-btn"
                  onClick={() => onRestoreTask(task.id)}
                  disabled={busyTaskId === task.id}
                >
                  <ReloadOutlined />
                  Restore
                </button>
                <button
                  type="button"
                  className="praxys-profile-action-btn praxys-profile-action-btn--danger"
                  onClick={() => onDeleteTask(task.id)}
                  disabled={busyTaskId === task.id}
                >
                  <DeleteOutlined />
                  Delete permanently
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>

    <section className="praxys-profile-archives">
      <div className="praxys-profile-section-header">
        <h3>Archived Knowledge Bases</h3>
        <span>{archivedKnowledgeBases.length} KB{archivedKnowledgeBases.length === 1 ? '' : 's'}</span>
      </div>

      {kbLoading ? (
        <div className="praxys-view-empty">Loading archived knowledge bases…</div>
      ) : archivedKnowledgeBases.length === 0 ? (
        <div className="praxys-view-empty">No archived knowledge bases yet.</div>
      ) : (
        <div className="praxys-profile-archive-list">
          {archivedKnowledgeBases.map((kb) => {
            const archivedAt = kb.updated_at || kb.created_at;
            return (
              <div key={kb.id} className="praxys-profile-archive-item">
                <div className="praxys-profile-archive-copy">
                  <div className="praxys-profile-archive-topline">
                    <h4>{kb.name}</h4>
                    <span className="praxys-profile-archive-badge">{kb.status || 'ARCHIVED'}</span>
                  </div>
                  <p className="praxys-profile-archive-meta">
                    {archivedAt ? `Archived ${new Date(archivedAt).toLocaleString()}` : 'Archived knowledge base'}
                  </p>
                  <p className="praxys-profile-archive-summary">
                    {kb.description?.trim() || 'No description provided.'}
                  </p>
                  <p className="praxys-profile-archive-meta">
                    {(kb.document_count || 0)} document{(kb.document_count || 0) === 1 ? '' : 's'} • {(kb.chunk_count || 0)} chunk{(kb.chunk_count || 0) === 1 ? '' : 's'}
                  </p>
                </div>
                <div className="praxys-profile-archive-actions">
                  <button
                    type="button"
                    className="praxys-profile-action-btn"
                    onClick={() => onRestoreKnowledgeBase({ id: kb.id, name: kb.name })}
                    disabled={busyKbId === kb.id}
                  >
                    <ReloadOutlined />
                    Restore
                  </button>
                  <button
                    type="button"
                    className="praxys-profile-action-btn praxys-profile-action-btn--danger"
                    onClick={() => onDeleteKnowledgeBase({ id: kb.id, name: kb.name })}
                    disabled={busyKbId === kb.id}
                  >
                    <DeleteOutlined />
                    Delete permanently
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>

    <ApiTokensView user={user} />

    <SecretsManagerView user={user} />

    <ProfileConnectorsView user={user} />
  </div>
);

export default ProfileView;
